import { useRef, useState, type Dispatch, type SetStateAction } from 'react';
import { useFormContext, useWatch } from 'react-hook-form';
import { Image, Trash2 } from 'lucide-react';
import { toast } from '@/components/ui/use-toast';
import { ImageDisplayDialog, type ImageType } from '@/pages/caseManagement/processDetails/ImageDisplayDialog';
import { OneLineText } from '@/components/OneLineText';
import FormFieldComponent from '@/components/FormFieldComponent';
import { FormFieldSelect } from '@/components/FormFieldSelect';
import FormFieldPhone from '@/components/FormFieldPhone';
import { uploadAttemptMedia } from '@/services/homeInspectionAttemptService';
import {
  YES_NO_OPTIONS,
  HOUSE_EXTERIOR_OPTIONS,
  HOUSE_TYPE_OPTIONS,
  GARAGE_OPTIONS,
  ALLOWED_IMAGE_EXTENSIONS,
  ALLOWED_IMAGE_MIME_TYPES,
  isAllowedImageFile,
  MAX_ATTEMPT_IMAGES,
  MAX_IMAGE_SIZE_BYTES,
  type HiAttemptFormValues,
} from '../hiAttempt.schema';
const DISABLED_STYLE = 'disabled:opacity-100 text-black';

export type PreviewEntry = {
  objectUrl: string;
  name: string;
  mediaId: string;
  isExisting?: boolean;
};

type Props = {
  readOnly?: boolean;
  previews: PreviewEntry[];
  setPreviews: Dispatch<SetStateAction<PreviewEntry[]>>;
};

export const HiAttemptStep3PropertyInfo = ({ readOnly, previews, setPreviews }: Props) => {
  const { control, setValue, watch } = useFormContext<HiAttemptFormValues>();
  const houseExterior = useWatch({ control, name: 'houseExterior' });
  const sale = useWatch({ control, name: 'sale' });
  const mediaIds = watch('mediaIds') ?? [];

  const [uploading, setUploading] = useState(false);
  const [lightboxMediaId, setLightboxMediaId] = useState<string | null>(null);
  const fileInputRef = useRef<HTMLInputElement>(null);

  const isUploadDisabled = uploading || mediaIds.length >= MAX_ATTEMPT_IMAGES;

  const handleFileChange = async (e: React.ChangeEvent<HTMLInputElement>) => {
    const files = Array.from(e.target.files ?? []);
    if (!files.length) return;

    const remaining = MAX_ATTEMPT_IMAGES - mediaIds.length;
    if (files.length > remaining) {
      toast({
        description: `You can upload at most ${MAX_ATTEMPT_IMAGES} images total. ${mediaIds.length} already added.`,
        variant: 'destructive',
        duration: 5000,
      });
      if (fileInputRef.current) fileInputRef.current.value = '';
      return;
    }

    for (const file of files) {
      if (!isAllowedImageFile(file)) {
        toast({
          description: `${file.name}: unsupported file type. Allowed: JPEG, PNG, HEIF, HEIC, RAW.`,
          variant: 'destructive',
          duration: 5000,
        });
        if (fileInputRef.current) fileInputRef.current.value = '';
        return;
      }
      if (file.size > MAX_IMAGE_SIZE_BYTES) {
        toast({
          description: `${file.name}: file size exceeds 10 MB limit.`,
          variant: 'destructive',
          duration: 5000,
        });
        if (fileInputRef.current) fileInputRef.current.value = '';
        return;
      }
    }

    setUploading(true);
    try {
      const newPreviews: PreviewEntry[] = [];
      const newIds: string[] = [];

      for (const file of files) {
        const mediaId = await uploadAttemptMedia(file);
        newIds.push(mediaId);
        newPreviews.push({ objectUrl: URL.createObjectURL(file), name: file.name, mediaId });
      }

      setValue('mediaIds', [...mediaIds, ...newIds], { shouldValidate: true });
      setPreviews((prev) => [...prev, ...newPreviews]);
    } catch {
      toast({
        description: 'Failed to upload one or more images. Please try again.',
        variant: 'destructive',
        duration: 5000,
      });
    } finally {
      setUploading(false);
      if (fileInputRef.current) fileInputRef.current.value = '';
    }
  };

  const removeImage = (index: number) => {
    const entry = previews[index];
    if (!entry.isExisting) URL.revokeObjectURL(entry.objectUrl);
    const updatedIds = mediaIds.filter((_, i) => i !== index);
    setValue('mediaIds', updatedIds, { shouldValidate: true });
    setPreviews((prev) => prev.filter((_, i) => i !== index));
  };

  return (
    <div className="flex flex-col w-full mt-11 gap-6">
      <div className="grid grid-cols-1 md:grid-cols-2 gap-6 text-black">
        <FormFieldSelect
          name="houseExterior"
          options={HOUSE_EXTERIOR_OPTIONS}
          placeholder="Select House Exterior"
          label="House Exterior"
          required
          disabled={readOnly}
        />
        {houseExterior === 'OTHER' && (
          <FormFieldComponent
            control={control}
            name="houseExteriorOther"
            label="Other Exterior"
            placeholder="Describe exterior"
            required
            disabled={readOnly}
            disabledStyle={readOnly ? DISABLED_STYLE : undefined}
          />
        )}
      </div>

      <div className="grid grid-cols-1 md:grid-cols-2 gap-6 text-black">
        <FormFieldSelect
          name="houseType"
          options={HOUSE_TYPE_OPTIONS}
          placeholder="Select House Type"
          label="House Type"
          required
          disabled={readOnly}
        />
        <FormFieldComponent
          control={control}
          name="numberOfUnits"
          label="Number of Units"
          placeholder="Number of Units"
          required
          type="number"
          allowNegativeNumbers={false}
          allowDecimals={false}
          props={{ min: '1', max: '100' }}
          disabled={readOnly}
          disabledStyle={readOnly ? DISABLED_STYLE : undefined}
        />
      </div>

      <div className="grid grid-cols-1 md:grid-cols-2 gap-6 text-black">
        <FormFieldSelect
          name="garage"
          options={GARAGE_OPTIONS}
          placeholder="Select Garage"
          label="Garage"
          required
          disabled={readOnly}
        />
        <FormFieldSelect name="sale" options={YES_NO_OPTIONS} placeholder="Select" label="Sale" required disabled={readOnly} />
      </div>

      {sale === 'YES' && (
        <div className="grid grid-cols-1 md:grid-cols-2 gap-6 text-black">
          <FormFieldComponent
            control={control}
            name="saleBy"
            label="By"
            placeholder="Sale by"
            required
            disabled={readOnly}
            disabledStyle={readOnly ? DISABLED_STYLE : undefined}
          />
          <FormFieldPhone<HiAttemptFormValues> name="salePhone" label="Sale Phone" disabled={readOnly} />
        </div>
      )}

      {/* Image upload section */}
      <div className="flex flex-col gap-2">
        {!readOnly && (
          <div className="flex flex-col gap-2">
            <button
              type="button"
              className={`bg-white cursor-pointer flex flex-row border-[1px] border-dashed rounded-md p-2 pr-[20px] gap-2 items-center justify-between h-[48px] w-full ${
                isUploadDisabled ? 'text-placeholder border-bordercolor' : 'text-primary border-[#92A1B3]'
              }`}
              onClick={() => { if (!isUploadDisabled) fileInputRef.current?.click(); }}
              disabled={isUploadDisabled}
            >
              <div className="flex cursor-pointer flex-row gap-2 items-center w-full">
                <Image className="h-5 w-5 shrink-0" />
                <span className="text-sm truncate">
                  {uploading ? 'Uploading…' : 'Click to upload image'}
                </span>
              </div>
              <input
                ref={fileInputRef}
                type="file"
                accept={[...ALLOWED_IMAGE_MIME_TYPES, ...ALLOWED_IMAGE_EXTENSIONS.map((ext) => `.${ext}`)].join(',')}
                multiple
                className="hidden"
                onChange={handleFileChange}
                disabled={isUploadDisabled}
              />
            </button>
            <div className="flex justify-end w-full mr-10 text-[13px]">
              {`${mediaIds.length} of ${MAX_ATTEMPT_IMAGES}`}
            </div>
          </div>
        )}

        <div className="grid grid-cols-2 gap-6 md:w-[80%] py-2">
          {previews.map((entry, index) => (
            <div
              key={entry.mediaId}
              className="flex justify-between h-[56px] bg-[#F5F5F5] rounded-md items-center p-4 text-primary"
            >
              <button
                type="button"
                className="flex gap-2 items-center cursor-pointer overflow-hidden"
                onClick={() => setLightboxMediaId(entry.mediaId)}
              >
                <Image className="h-5 w-5 text-primary shrink-0" />
                <OneLineText text={entry.name} className="flex mr-5" />
              </button>
              {!readOnly && (
                <button type="button" onClick={() => removeImage(index)} aria-label={`Remove ${entry.name}`}>
                  <Trash2 className="cursor-pointer text-error h-5 w-5 shrink-0" />
                </button>
              )}
            </div>
          ))}
        </div>

        {previews.length === 0 && (
          <div className="text-sm text-placeholder text-center py-2">No images added yet.</div>
        )}
      </div>

      {lightboxMediaId && (
        <ImageDisplayDialog
          attachmentId={lightboxMediaId}
          attachments={previews.map<ImageType>((entry) => ({
            id: entry.mediaId,
            name: entry.name,
            image: null,
            mediaId: entry.mediaId,
          }))}
          onClose={() => setLightboxMediaId(null)}
        />
      )}
    </div>
  );
};
